import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusApi } from "@/lib/apiClient";
import { BellRing, Phone, Mail, Loader2, CalendarClock } from "lucide-react";

function fmtDate(d) {
  try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return d; }
}

export default function RecallAlert({ onGoToComune }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ recalls: [], overdue: 0, due_today: 0, total: 0, today: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await StatusApi.listRecalls()); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const alertCount = (data.overdue || 0) + (data.due_today || 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="open-recalls-btn" className="h-9 relative">
        <BellRing className="h-4 w-4 md:mr-1.5" /> <span className="hidden md:inline">Richiami</span>
        {alertCount > 0 && (
          <span data-testid="recall-badge"
            className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
            {alertCount}
          </span>
        )}
      </Button>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="recalls-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" /> Richiami in scadenza
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 text-xs shrink-0">
          <span className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200" data-testid="recall-overdue">{data.overdue || 0} scaduti</span>
          <span className="px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200" data-testid="recall-today">{data.due_today || 0} oggi</span>
          <span className="px-2 py-1 rounded bg-muted text-muted-foreground border border-border">{data.total || 0} totali</span>
        </div>
        <div className="overflow-auto flex-1 mt-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
            </div>
          ) : data.recalls.length === 0 ? (
            <div className="text-sm text-muted-foreground py-16 text-center" data-testid="recalls-empty">
              Nessun richiamo programmato. Imposta una data nella colonna "Da richiamare".
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="recalls-list">
              {data.recalls.map((r) => (
                <li key={r.id} className={`flex items-center gap-3 px-2 py-2.5 ${r.overdue ? "bg-red-50/60" : r.today ? "bg-orange-50/60" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <button onClick={() => { onGoToComune && onGoToComune(r); setOpen(false); }}
                      className="font-semibold text-sm truncate hover:text-primary text-left block w-full" data-testid={`recall-company-${r.id}`}>
                      {r.company_name}
                    </button>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>{r.comune}</span>
                      {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="h-3 w-3" />{r.phone}</a>}
                      {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="h-3 w-3" />{r.email}</a>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-xs font-semibold ${r.overdue ? "text-red-600" : r.today ? "text-orange-600" : "text-foreground"}`}>{fmtDate(r.recall_date)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.overdue ? "scaduto" : r.today ? "oggi" : "in programma"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
